import React from "react";
import {
  findConfigurationByKey,
  getDayZero,
  getDayDocumentsInit,
  updateConfig,
} from "../../services/configuration.service.ts";
import ModalContainer from "../ModalContainer.tsx";
import EditTimeAppoinment from "../config/EditTimeAppoinment.tsx";
import Loader from "../Loader.tsx";
import EditCheckTime from "../config/EditCheckTime.tsx";
import { readPositions } from "../../services/position.service.ts";
import { useNavigate } from "react-router-dom";
import ColorScheduleForm from "../scheduleColors/ColorScheduleForm.tsx";
import AddModulesAppPermissions from "../config/AddModulesAppPermissions.tsx";
import EditDayZero from "../config/EditDayZero.tsx";
import EditDayDocumentsInit from "../config/EditDayDocumentsInit.tsx";
import QuickAnswersManager from "../config/QuickAnswersManager.tsx";
import {
  getScheduleEditConfig,
  updateScheduleEditConfig,
  sendFaltasReportConsolidadoWhatsApp,
  downloadFaltasReportConsolidadoPdf,
} from "../../services/branchSchedule.service.ts";
import { readBranches } from "../../services/branch.service.ts";
import { autocompletePartners } from "../../services/partner.service.ts";
import {
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";

const styles = {
  section: {
    marginBottom: "32px",
    maxWidth: "800px",
  },
  sectionHeader: {
    marginBottom: "12px",
    background: "#f9fafb",
    borderRadius: "8px",
    padding: "12px 16px",
    border: "1px solid #e5e7eb",
    maxWidth: "800px",
  },
  sectionTitle: {
    margin: 0,
    marginBottom: "4px",
    color: "#374151",
    fontSize: "16px",
    fontWeight: "600" as const,
  },
  sectionDescription: {
    margin: 0,
    color: "#6b7280",
    fontSize: "12px",
  },
  card: {
    background: "#fff",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    overflow: "hidden" as const,
    maxWidth: "800px",
  },
};

/** Teléfono de persona → formato WhatsApp México (521…). */
function normalizeMxWhatsApp(phone: string | null | undefined): string | null {
  if (phone == null || phone === "") return null;
  const d = String(phone).replace(/\D/g, "");
  if (d.length === 10) return `521${d}`;
  if (d.length === 12 && d.startsWith("52")) return d;
  if (d.length === 13 && d.startsWith("521")) return d;
  return null;
}

function normalizeSearchText(text: string | null | undefined): string {
  return (text || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function toDisplayCase(text: string | null | undefined): string {
  const clean = normalizeSearchText(text);
  if (!clean) return "";
  return clean.replace(/\b\w/g, (c) => c.toUpperCase());
}

function partnerFullName(p: any): string {
  const person = p?.person;
  if (!person) return `Colaborador #${p?.id ?? ""}`;
  return [
    toDisplayCase(person.firstName),
    toDisplayCase(person.lastName),
    toDisplayCase(person.secondLastName),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
}

function formatPhoneForDisplay(wa: string): string {
  if (wa.startsWith("521") && wa.length === 13) {
    const local = wa.slice(3);
    return `+52 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }
  return wa;
}

function toAutoRecipientConfig(partner: any) {
  return {
    id: partner?.id,
    name: partnerFullName(partner),
    phoneNumber: normalizeMxWhatsApp(partner?.person?.phone),
    branchName: toDisplayCase(partner?.candidate?.branch?.name) || "",
    positionName: toDisplayCase(partner?.candidate?.vacancy?.position?.name) || "",
  };
}

const ConfigSection = () => {
  const [appoinmentTime, setAppoinmentTime] = React.useState(10);
  const [modalAppoinment, setModalAppoinment] = React.useState<boolean>(false);
  const [modalColors, setModalColors] = React.useState<boolean>(false);

  const [modalAddModulesAppPermissions, setModalAddModulesAppPermissions] =
    React.useState<boolean>(false);

  const [modalDayZero, setModalDayZero] = React.useState<boolean>(false);
  const [dayZeroDate, setDayZeroDate] = React.useState<string | null>(null);
  const [modalDayDocumentsInit, setModalDayDocumentsInit] = React.useState<boolean>(false);
  const [dayDocumentsInitDate, setDayDocumentsInitDate] = React.useState<string | null>(null);
  const [modalQuickAnswers, setModalQuickAnswers] = React.useState<boolean>(false);
  const [modalFaltasReport, setModalFaltasReport] = React.useState<boolean>(false);
  const [faltasReportDate, setFaltasReportDate] = React.useState<string>(() => new Date().toISOString().slice(0, 10));
  const [faltasReportShift, setFaltasReportShift] = React.useState<string>("Matutino");
  const [faltasReportPartnerOptions, setFaltasReportPartnerOptions] = React.useState<any[]>([]);
  const [faltasReportSelectedPartners, setFaltasReportSelectedPartners] = React.useState<any[]>([]);
  const [faltasReportSearch, setFaltasReportSearch] = React.useState("");
  const [faltasReportPartnerLoad, setFaltasReportPartnerLoad] = React.useState(false);
  const [faltasReportSending, setFaltasReportSending] = React.useState<boolean>(false);
  const [faltasReportDownloading, setFaltasReportDownloading] = React.useState<boolean>(false);
  const [faltasReportAutoSendEnabled, setFaltasReportAutoSendEnabled] = React.useState(false);
  const [faltasReportAutoSendSaving, setFaltasReportAutoSendSaving] = React.useState(false);
  const [faltasReportAutoRecipients, setFaltasReportAutoRecipients] = React.useState<any[]>([]);
  const [faltasReportAutoRecipientsSearch, setFaltasReportAutoRecipientsSearch] = React.useState("");
  const [faltasReportAutoRecipientsOptions, setFaltasReportAutoRecipientsOptions] = React.useState<any[]>([]);
  const [faltasReportAutoRecipientsLoad, setFaltasReportAutoRecipientsLoad] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: "success" | "error" }>({ open: false, message: "", severity: "success" });

  const [assisitanceTime, setAssisitanceTime] = React.useState();
  const [modalAssisitance, setModalAssisitance] =
    React.useState<boolean>(false);
  const [modalOrderPosition, setModalOrderPosition] =
    React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  const [branches, setBranches] = React.useState<any[]>([]);
  const [scheduleEditConfigs, setScheduleEditConfigs] = React.useState<Record<number, { enabled: boolean; autoDisableAt: string | null; durationMinutes: number }>>({});
  const [originalScheduleEditConfigs, setOriginalScheduleEditConfigs] = React.useState<Record<number, { enabled: boolean; autoDisableAt: string | null; durationMinutes: number }>>({});
  const [modalScheduleEdit, setModalScheduleEdit] = React.useState<boolean>(false);
  const [searchBranch, setSearchBranch] = React.useState<string>("");
  const [branchContextMenu, setBranchContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
    branch: any;
  } | null>(null);

  const fetchDependencies = async () => {
    setLoading(true);
    try {
      const assistanceTimeResponse = await findConfigurationByKey("assistance");
      if (assistanceTimeResponse?.value)
        setAssisitanceTime(assistanceTimeResponse?.value);

      try {
        const dayZero = await getDayZero();
        if (dayZero) {
          const date = new Date(dayZero);
          const formattedDate = date.toISOString().split("T")[0];
          setDayZeroDate(formattedDate);
        }
      } catch (error) {
        console.log("Error al obtener dayZero:", error);
      }

      try {
        const dayDocumentsInit = await getDayDocumentsInit();
        if (dayDocumentsInit) {
          const date = new Date(dayDocumentsInit);
          setDayDocumentsInitDate(date.toISOString().split("T")[0]);
        }
      } catch (error) {
        console.log("Error al obtener día inicio documentos:", error);
      }

      try {
        const branchesResponse = await readBranches();
        setBranches(branchesResponse || []);

        const configs: Record<number, { enabled: boolean; autoDisableAt: string | null; durationMinutes: number }> = {};

        await Promise.all(
          (branchesResponse || []).map(async (branch) => {
            try {
              const editConfig = await getScheduleEditConfig(branch.id);
              configs[branch.id] = {
                enabled: Boolean(editConfig?.enabled),
                autoDisableAt: editConfig?.autoDisableAt || null,
                durationMinutes: 120,
              };
            } catch (error) {
              console.log(`Error al obtener configuración de edición de horario para sucursal ${branch.id}:`, error);
              configs[branch.id] = {
                enabled: true,
                autoDisableAt: null,
                durationMinutes: 120,
              };
            }
          })
        );

        setScheduleEditConfigs(configs);
        setOriginalScheduleEditConfigs(configs);
      } catch (error) {
        console.log("Error al obtener sucursales o configuración de edición de horario:", error);
      }
    } catch (error) {
      setAppoinmentTime(10);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const [positions, setPositions] = React.useState([]);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const response = await readPositions();
      setPositions(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchDependencies();
    fetchPositions();
  }, []);

  React.useEffect(() => {
    if (!modalFaltasReport) return;
    setFaltasReportSelectedPartners([]);
    setFaltasReportPartnerOptions([]);
    setFaltasReportSearch("");
    setFaltasReportAutoRecipientsSearch("");
    setFaltasReportAutoRecipientsOptions([]);
    (async () => {
      try {
        const cfg = await findConfigurationByKey("faltas-report-whatsapp-auto");
        setFaltasReportAutoSendEnabled(Boolean(cfg?.value?.enabled));
        setFaltasReportAutoRecipients(
          Array.isArray(cfg?.value?.recipients) ? cfg.value.recipients : []
        );
      } catch {
        setFaltasReportAutoSendEnabled(false);
        setFaltasReportAutoRecipients([]);
      }
    })();
  }, [modalFaltasReport]);

  React.useEffect(() => {
    if (!modalFaltasReport) return;
    const query = faltasReportSearch.trim();

    const handler = window.setTimeout(async () => {
      setFaltasReportPartnerLoad(true);
      try {
        const res = await autocompletePartners(query);
        const filtered = (res || []).filter((p: any) =>
          normalizeMxWhatsApp(p?.person?.phone)
        );
        setFaltasReportPartnerOptions(filtered);
      } catch (e) {
        console.log("Error al buscar colaboradores para WhatsApp:", e);
        setFaltasReportPartnerOptions([]);
      } finally {
        setFaltasReportPartnerLoad(false);
      }
    }, 350);

    return () => window.clearTimeout(handler);
  }, [modalFaltasReport, faltasReportSearch]);

  React.useEffect(() => {
    if (!modalFaltasReport) return;
    const query = faltasReportAutoRecipientsSearch.trim();

    const handler = window.setTimeout(async () => {
      setFaltasReportAutoRecipientsLoad(true);
      try {
        const res = await autocompletePartners(query);
        const filtered = (res || []).filter((p: any) =>
          normalizeMxWhatsApp(p?.person?.phone)
        );
        setFaltasReportAutoRecipientsOptions(filtered);
      } catch (e) {
        console.log("Error al buscar destinatarios automáticos:", e);
        setFaltasReportAutoRecipientsOptions([]);
      } finally {
        setFaltasReportAutoRecipientsLoad(false);
      }
    }, 350);

    return () => window.clearTimeout(handler);
  }, [modalFaltasReport, faltasReportAutoRecipientsSearch]);

  const navigate = useNavigate();

  const handleOrderPosition = () => {
    navigate("order-puestos/", {
      state: { positionsList: positions },
    });
  };

  const ConfigItem = ({
    title,
    description,
    value,
    onEdit,
    buttonText = "Editar",
    icon,
  }: {
    title: string;
    description?: string;
    value?: string | React.ReactNode;
    onEdit: () => void;
    buttonText?: string;
    icon?: string;
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderBottom: "1px solid #e5e7eb",
        transition: "background-color 0.15s ease",
        cursor: "pointer",
      }}
      onClick={onEdit}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#f9fafb";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: 1,
          minWidth: 0,
        }}
      >
        {icon && (
          <i
            className={`fa-solid ${icon}`}
            style={{
              color: "#6b7280",
              fontSize: "14px",
              width: "18px",
              textAlign: "center",
              flexShrink: 0,
            }}
          ></i>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: "600",
              color: "#374151",
              fontSize: "14px",
              marginBottom: "2px",
            }}
          >
            {title}
          </div>
          {value && (
            <div
              style={{
                color: "#6b7280",
                fontSize: "12px",
                marginTop: "2px",
              }}
            >
              {value}
            </div>
          )}
          {description && !value && (
            <div
              style={{
                color: "#9ca3af",
                fontSize: "12px",
                marginTop: "2px",
              }}
            >
              {description}
            </div>
          )}
        </div>
      </div>
      <div style={{ marginLeft: "12px", flexShrink: 0 }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          style={{
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            color: "#374151",
            padding: "6px 12px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.15s ease",
            minWidth: "70px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#e5e7eb";
            e.currentTarget.style.borderColor = "#d1d5db";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#f3f4f6";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );

  const SectionHeader = ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => (
    <div style={styles.sectionHeader}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <p style={styles.sectionDescription}>{description}</p>
    </div>
  );

  const SectionContainer = ({
    id,
    title,
    description,
    items,
    loading,
  }: {
    id: string;
    title: string;
    description: string;
    items: React.ReactNode[];
    loading: boolean;
  }) => (
    <div id={id} style={styles.section}>
      <SectionHeader title={title} description={description} />
      <div style={styles.card}>
        {items.map((item, index) => (
          <React.Fragment key={index}>{item}</React.Fragment>
        ))}
      </div>
      <Loader isLoading={loading} />
    </div>
  );

  const formatDayZeroDate = (date: string | null) => {
    if (!date) return "No configurada";
    return `Fecha actual: ${new Date(date + "T00:00:00").toLocaleDateString(
      "es-ES",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    )}`;
  };

  const formatDayDocumentsInitDate = (date: string | null) => {
    if (!date) return "No configurada";
    return `Fecha actual: ${new Date(date + "T00:00:00").toLocaleDateString(
      "es-ES",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    )}`;
  };

  const toggleScheduleEdit = () => {
    setModalScheduleEdit(true);
  };

  const handleChangeBranchEnabled = (branchId: number, enabled: boolean) => {
    setScheduleEditConfigs((prev) => ({
      ...prev,
      [branchId]: {
        ...(prev[branchId] || { durationMinutes: 120, autoDisableAt: null }),
        enabled,
      },
    }));
  };

  const handleChangeBranchDuration = (branchId: number, duration: number) => {
    setScheduleEditConfigs((prev) => ({
      ...prev,
      [branchId]: {
        ...(prev[branchId] || { enabled: true, autoDisableAt: null }),
        durationMinutes: duration > 0 ? duration : 1,
      },
    }));
  };

  const handleSaveScheduleEditConfig = async () => {
    try {
      setLoading(true);

      await Promise.all(
        branches.map(async (branch) => {
          const cfg = scheduleEditConfigs[branch.id];
          const original = originalScheduleEditConfigs[branch.id];
          if (!cfg) return;

          if (
            original &&
            cfg.enabled === original.enabled &&
            cfg.durationMinutes === original.durationMinutes
          ) {
            return;
          }

          const payload: any = {
            enabled: cfg.enabled,
          };

          if (cfg.enabled && cfg.durationMinutes > 0) {
            payload.durationMinutes = cfg.durationMinutes;
          }

          await updateScheduleEditConfig(branch.id, payload);
        })
      );

      setModalScheduleEdit(false);
      setOriginalScheduleEditConfigs(scheduleEditConfigs);
      setSearchBranch("");
    } catch (error) {
      console.log("Error actualizando configuración de edición de horario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAll = (enabled: boolean) => {
    const updatedConfigs: Record<number, { enabled: boolean; autoDisableAt: string | null; durationMinutes: number }> = {};
    
    branches.forEach((branch) => {
      const currentCfg = scheduleEditConfigs[branch.id];
      updatedConfigs[branch.id] = {
        enabled,
        autoDisableAt: currentCfg?.autoDisableAt || null,
        durationMinutes: currentCfg?.durationMinutes || 120,
      };
    });

    setScheduleEditConfigs(updatedConfigs);
  };

  const handleBranchContextMenu = (event: React.MouseEvent, branch: any) => {
    event.preventDefault();
    setBranchContextMenu(
      branchContextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            branch,
          }
        : null
    );
  };

  const handleCloseBranchContextMenu = () => {
    setBranchContextMenu(null);
  };

  const handleOpenBranchSchedule = (branch: any) => {
    const url = `${window.location.origin}/sucursales/horario/${branch?.id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const filteredBranches = branches.filter((branch) =>
    branch.name?.toLowerCase().includes(searchBranch.toLowerCase())
  );

  const sectionsConfig = [
    {
      id: "general",
      title: "General",
      description: "Ver y actualizar la configuración general del sistema.",
      items: [
        {
          title: "Tiempo por cita",
          value: `${appoinmentTime} minutos`,
          onEdit: () => setModalAppoinment(true),
          icon: "fa-clock",
        },
        {
          title: "Minutos para registrar asistencia",
          value: "",
          onEdit: () => setModalAssisitance(true),
          icon: "fa-clock",
        },
        {
          title: "Edición de horario de sucursales",
          value: "Configurar por sucursal",
          onEdit: toggleScheduleEdit,
          icon: "fa-lock-open",
        },
        {
          title: "Colores para el reloj checador",
          description: "Configurar colores para ver el reloj checador",
          onEdit: () => setModalColors(true),
          icon: "fa-palette",
        },
        {
          title: "Temporadas bloqueadas",
          description: "Administrar el listado de temporadas bloqueadas",
          onEdit: () => navigate("/configuracion/dias-obligatorios"),
          icon: "fa-calendar-xmark",
        },
        {
          title: "Días festivos",
          description: "Configurar días festivos con bonificaciones salariales",
          onEdit: () => navigate("/configuracion/dias-festivos"),
          icon: "fa-calendar-check",
        },
        {
          title: "Accesos a módulos de la aplicación",
          description: "Configurar accesos a módulos de la aplicación",
          onEdit: () => setModalAddModulesAppPermissions(true),
          icon: "fa-shield-halved",
        },
        {
          title: "Servidor SMTP",
          description: "Configurar servidor SMTP para el envío de correos",
          onEdit: () => navigate("/emails/configuracion"),
          icon: "fa-envelope",
        },
        {
          title: "Plantillas de correos",
          description: "Ver y administrar la tabla de plantillas de correo",
          onEdit: () => navigate("/emails/plantillas"),
          buttonText: "Ver",
          icon: "fa-file-lines",
        },
        {
          title: "Día inicio documentos",
          description: "Fecha de inicio para contar los días máx. de subida de documentos (ej. día init + 5 días = fecha límite)",
          value: formatDayDocumentsInitDate(dayDocumentsInitDate),
          onEdit: () => setModalDayDocumentsInit(true),
          icon: "fa-file-circle-plus",
        },
        {
          title: "Preguntas Frecuentes (FAQs)",
          description: "Gestionar preguntas frecuentes y sus respuestas",
          onEdit: () => navigate("/configuracion/faqs"),
          icon: "fa-circle-question",
        },
        {
          title: "Respuestas rápidas",
          description: "Administrar respuestas rápidas para incidencias",
          onEdit: () => setModalQuickAnswers(true),
          icon: "fa-bolt",
        },
        {
          title: "Reporte de faltas por WhatsApp",
          description:
            "Reporte de faltas por whatsapp",
          onEdit: () => setModalFaltasReport(true),
          buttonText: "Enviar",
          icon: "fa-paper-plane",
        },
      ],
    },
    {
      id: "ordenamiento",
      title: "Ordenamiento General",
      description: "Ordenamiento visualizado",
      items: [
        {
          title: "Orden de puestos",
          description: "Drag and drop para ordenar los puestos",
          onEdit: handleOrderPosition,
          icon: "fa-grip-vertical",
        },
      ],
    },
    {
      id: "insignias",
      title: "Insignias",
      description: "Panel de insignias para trabajadores",
      items: [
        {
          title: "Ver todas las insignias",
          onEdit: () => navigate("/insignias"),
          buttonText: "Panel",
          icon: "fa-award",
        },
      ],
    },
  ];

  return (
    <div id="bsSpyContent" style={{ width: "100%" }}>
      {sectionsConfig.map((section) => (
        <SectionContainer
          key={section.id}
          id={section.id}
          title={section.title}
          description={section.description}
          loading={loading}
          items={section.items.map((item, index) => (
            <ConfigItem
              key={index}
              title={item.title}
              description={item.description}
              value={item.value}
              onEdit={item.onEdit}
              buttonText={item.buttonText}
              icon={item.icon}
            />
          ))}
        />
      ))}
      <ModalContainer
        title="Editar el tiempo de la cita"
        open={modalAppoinment}
        closeFn={() => setModalAppoinment(false)}
        children={
          <EditTimeAppoinment
            closeFn={() => setModalAppoinment(false)}
            refreshFn={() => fetchDependencies()}
            currentTime={appoinmentTime}
            setLoading={(value) => setLoading(value)}
          />
        }
      />
      <ModalContainer
        title="Editar los minutos para registrar asistencia"
        open={modalAssisitance}
        closeFn={() => setModalAssisitance(false)}
        children={
          <EditCheckTime
            closeFn={() => setModalAssisitance(false)}
            refreshFn={() => fetchDependencies()}
            setLoading={(value) => setLoading(value)}
            assisitanceTime={assisitanceTime}
          />
        }
      />
      <ModalContainer
        title="Configuración de colores"
        open={modalColors}
        closeFn={() => setModalColors(false)}
        size="lg"
        children={
          <ColorScheduleForm
            closeFn={() => setModalColors(false)}
            setLoading={(value) => setLoading(value)}
          />
        }
      />
      {null}

      <ModalContainer
        title="Modulos de la aplicación"
        open={modalAddModulesAppPermissions}
        closeFn={() => setModalAddModulesAppPermissions(false)}
        size="xl"
        children={
          <AddModulesAppPermissions
            closeFn={() => setModalAddModulesAppPermissions(false)}
            setLoading={(value) => setLoading(value)}
          />
        }
      />
      <ModalContainer
        title="Editar Día inicio documentos"
        open={modalDayDocumentsInit}
        closeFn={() => setModalDayDocumentsInit(false)}
        children={
          <EditDayDocumentsInit
            closeFn={() => setModalDayDocumentsInit(false)}
            refreshFn={() => fetchDependencies()}
            setLoading={(value) => setLoading(value)}
            currentDate={dayDocumentsInitDate}
          />
        }
      />
      <ModalContainer
        title="Configuración de edición de horario por sucursal"
        open={modalScheduleEdit}
        closeFn={() => {
          setModalScheduleEdit(false);
          setSearchBranch("");
        }}
        children={
          <div className="p-3">
            <div className="mb-3">
              <div className="text-muted fs-12px mb-3">
                Cuando la edición está deshabilitada, el horario de la sucursal se muestra en solo lectura
                (no se puede arrastrar, redimensionar ni eliminar elementos).
              </div>
              
              {/* Buscador y switch Todos en fila */}
              <div className="mb-3 d-flex align-items-center" style={{ gap: 12 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar sucursal..."
                  value={searchBranch}
                  onChange={(e) => setSearchBranch(e.target.value)}
                  style={{ 
                    flex: 1,
                    borderRadius: "8px",
                    backgroundColor: "#f3f4f6",
                    borderColor: "#d1d5db"
                  }}
                />
                <div className="d-flex align-items-center" style={{ gap: 8, whiteSpace: "nowrap" }}>
                  <span className="fw-600">Todos</span>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="toggleAllSwitch"
                      checked={branches.length > 0 && branches.every((branch) => scheduleEditConfigs[branch.id]?.enabled !== false)}
                      onChange={(e) => handleToggleAll(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-3" style={{ maxHeight: 400, overflowY: "auto" }}>
              {filteredBranches.length === 0 ? (
                <div className="text-center text-muted p-3">
                  No se encontraron sucursales
                </div>
              ) : (
                filteredBranches.map((branch) => {
                  const cfg = scheduleEditConfigs[branch.id];
                  return (
                    <div
                      key={branch.id}
                      className="border rounded p-2 mb-2 d-flex align-items-center justify-content-between"
                      style={{ borderRadius: "8px" }}
                      onContextMenu={(e) => handleBranchContextMenu(e, branch)}
                    >
                      <div className="fw-600">{branch.name}</div>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`scheduleEditEnabledSwitch-${branch.id}`}
                          checked={cfg?.enabled ?? true}
                          onChange={(e) =>
                            handleChangeBranchEnabled(branch.id, e.target.checked)
                          }
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <Menu
              open={branchContextMenu !== null}
              onClose={handleCloseBranchContextMenu}
              anchorReference="anchorPosition"
              anchorPosition={
                branchContextMenu !== null
                  ? { top: branchContextMenu.mouseY, left: branchContextMenu.mouseX }
                  : undefined
              }
              PaperProps={{
                style: {
                  backgroundColor: "#ffffff",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                  borderRadius: "8px",
                },
              }}
            >
              <MenuItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  fontSize: "16px",
                }}
                onClick={() => {
                  const branch = branchContextMenu?.branch;
                  handleCloseBranchContextMenu();
                  handleOpenBranchSchedule(branch);
                }}
              >
                <i
                  className="fa-regular fa-calendar-days"
                  style={{ marginRight: "12px", fontSize: "18px" }}
                ></i>
                Ir al horario
              </MenuItem>
            </Menu>

            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => {
                  setModalScheduleEdit(false);
                  setSearchBranch("");
                  handleCloseBranchContextMenu();
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveScheduleEditConfig}
              >
                Guardar
              </button>
            </div>
          </div>
        }
      />
      <ModalContainer
        title="Respuestas Rápidas"
        open={modalQuickAnswers}
        closeFn={() => setModalQuickAnswers(false)}
        size="lg"
        children={
          <QuickAnswersManager
            closeFn={() => setModalQuickAnswers(false)}
            setLoading={(value) => setLoading(value)}
          />
        }
      />
      <ModalContainer
        title="Enviar reporte de faltas por WhatsApp"
        open={modalFaltasReport}
        size="lg"
        closeFn={() =>
          !faltasReportSending &&
          !faltasReportDownloading &&
          setModalFaltasReport(false)
        }
        children={
          <Box sx={{ p: 0.5 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                borderLeft: "4px solid #25D366",
                bgcolor: "#f0fdf4",
                border: "1px solid #bbf7d0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <i
                  className="fa-brands fa-whatsapp"
                  style={{ color: "#25D366", fontSize: "1.5rem", marginTop: 2 }}
                />
                <div>
                  <Typography variant="subtitle2" fontWeight={700} color="#166534" gutterBottom>
                    Un solo PDF para todas las sucursales
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Se genera el concentrado del turno elegido y se envía por WhatsApp a los colaboradores
                    que selecciones. El mismo archivo se envía a cada destinatario.
                  </Typography>
                </div>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                mb: 2.5,
                borderRadius: 2.5,
                border: "1px solid #dbe2ea",
                bgcolor: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.25,
                  pb: 1.25,
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                  Envío automático
                </Typography>
                <Chip
                  size="small"
                  label={faltasReportAutoSendEnabled ? "Activo" : "Inactivo"}
                  color={faltasReportAutoSendEnabled ? "success" : "default"}
                  variant={faltasReportAutoSendEnabled ? "filled" : "outlined"}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={faltasReportAutoSendEnabled}
                    onChange={async (_, checked) => {
                      setFaltasReportAutoSendEnabled(checked);
                      setFaltasReportAutoSendSaving(true);
                      try {
                        await updateConfig("faltas-report-whatsapp-auto", {
                          enabled: checked,
                          recipients: faltasReportAutoRecipients,
                          phoneNumbers: faltasReportAutoRecipients
                            .map((r) => r?.phoneNumber)
                            .filter(Boolean),
                        });
                        setSnackbar({
                          open: true,
                          message: checked
                            ? "Envío automático activado."
                            : "Envío automático desactivado.",
                          severity: "success",
                        });
                      } catch (err: any) {
                        setFaltasReportAutoSendEnabled((prev) => !prev);
                        setSnackbar({
                          open: true,
                          message:
                            err?.response?.data?.message ||
                            "No se pudo actualizar el envío automático.",
                          severity: "error",
                        });
                      } finally {
                        setFaltasReportAutoSendSaving(false);
                      }
                    }}
                    disabled={faltasReportAutoSendSaving || faltasReportSending}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      Envío automático de reporte de faltas
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                      Activa el envío automático de reportes de faltas por WhatsApp: se envía uno por la mañana y otro por la tarde.
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, alignItems: "flex-start" }}
              />
              <Box sx={{ mt: 1.75 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                  Destinatarios del envío automático
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {faltasReportAutoRecipients.length} seleccionado(s)
                  </Typography>
                </Box>
                <Autocomplete
                  multiple
                  options={faltasReportAutoRecipientsOptions}
                  value={faltasReportAutoRecipients}
                  onChange={(_, v, reason) => {
                    const mapped = v
                      .map((p: any) => (p?.phoneNumber ? p : toAutoRecipientConfig(p)))
                      .filter((x: any) => Boolean(x?.phoneNumber));
                    setFaltasReportAutoRecipients(mapped);
                    if (reason === "selectOption") {
                      setFaltasReportAutoRecipientsSearch("");
                    }
                  }}
                  inputValue={faltasReportAutoRecipientsSearch}
                  onInputChange={(_, value, reason) => {
                    if (reason === "input" || reason === "clear") {
                      setFaltasReportAutoRecipientsSearch(value);
                    }
                  }}
                  getOptionLabel={(option: any) => option?.name || partnerFullName(option)}
                  isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
                  disabled={faltasReportAutoSendSaving || faltasReportSending}
                  loading={faltasReportAutoRecipientsLoad}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar colaboradores para envío automático…"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {faltasReportAutoRecipientsLoad ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  filterOptions={(options) => options}
                  renderOption={(props, option: any) => {
                    const wa = option?.phoneNumber || normalizeMxWhatsApp(option?.person?.phone);
                    const branch = option?.branchName || toDisplayCase(option?.candidate?.branch?.name);
                    const position = option?.positionName || toDisplayCase(option?.candidate?.vacancy?.position?.name);
                    return (
                      <li {...props} key={option.id}>
                        <Box sx={{ py: 0.25 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {option?.name || partnerFullName(option)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {branch || "Sin sucursal"} {" · "} {position || "Sin puesto"}
                            {wa ? ` · ${formatPhoneForDisplay(wa)}` : ""}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    style={{ minWidth: 150 }}
                    disabled={faltasReportAutoSendSaving || faltasReportSending}
                    onClick={async () => {
                      setFaltasReportAutoSendSaving(true);
                      try {
                        await updateConfig("faltas-report-whatsapp-auto", {
                          enabled: faltasReportAutoSendEnabled,
                          recipients: faltasReportAutoRecipients,
                          phoneNumbers: faltasReportAutoRecipients
                            .map((r) => r?.phoneNumber)
                            .filter(Boolean),
                        });
                        setSnackbar({
                          open: true,
                          message: "Destinatarios automáticos guardados.",
                          severity: "success",
                        });
                      } catch (err: any) {
                        setSnackbar({
                          open: true,
                          message:
                            err?.response?.data?.message ||
                            "No se pudieron guardar los destinatarios automáticos.",
                          severity: "error",
                        });
                      } finally {
                        setFaltasReportAutoSendSaving(false);
                      }
                    }}
                  >
                    {faltasReportAutoSendSaving ? "Guardando..." : "Guardar"}
                  </button>
                </Box>
              </Box>
            </Paper>

            <Accordion defaultExpanded={false} sx={{ borderRadius: 2, boxShadow: "none", border: "1px solid #e5e7eb", mb: 1 }}>
              <AccordionSummary
                expandIcon={<i className="fa-solid fa-chevron-down" style={{ fontSize: 12 }} />}
                sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75 } }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Envío manual
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 2.5,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  Fecha del reporte
                </Typography>
                <input
                  type="date"
                  className="form-control"
                  style={{ borderRadius: 8 }}
                  value={faltasReportDate}
                  onChange={(e) => setFaltasReportDate(e.target.value)}
                  disabled={faltasReportSending || faltasReportDownloading}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  Turno
                </Typography>
                <select
                  className="form-select"
                  style={{ borderRadius: 8 }}
                  value={faltasReportShift}
                  onChange={(e) => setFaltasReportShift(e.target.value)}
                  disabled={faltasReportSending || faltasReportDownloading}
                >
                  <option value="Matutino">Matutino</option>
                  <option value="Intermedio">Intermedio</option>
                  <option value="Vespertino">Vespertino</option>
                </select>
              </Box>
            </Box>

            <Typography variant="subtitle2" fontWeight={700} color="text.primary" gutterBottom>
              Destinatarios
            </Typography>
            <Autocomplete
              multiple
              options={faltasReportPartnerOptions}
              value={faltasReportSelectedPartners}
              onChange={(_, v, reason) => {
                setFaltasReportSelectedPartners(v);
                if (reason === "selectOption") {
                  setFaltasReportSearch("");
                }
              }}
              inputValue={faltasReportSearch}
              onInputChange={(_, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setFaltasReportSearch(value);
                }
              }}
              getOptionLabel={(option) => partnerFullName(option)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={faltasReportSending || faltasReportDownloading}
              loading={faltasReportPartnerLoad}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar y elegir colaboradores…"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fafafa" },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {faltasReportPartnerLoad ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              filterOptions={(options) => options}
              noOptionsText={
                faltasReportSearch.trim()
                  ? "Sin coincidencias"
                  : "Sin colaboradores disponibles"
              }
              renderOption={(props, option) => {
                const wa = normalizeMxWhatsApp(option?.person?.phone);
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ py: 0.25 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {partnerFullName(option)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {toDisplayCase(option?.candidate?.branch?.name) || "Sin sucursal"}
                        {" · "}
                        {toDisplayCase(option?.candidate?.vacancy?.position?.name) || "Sin puesto"}
                        {wa ? ` · ${formatPhoneForDisplay(wa)}` : ""}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />

            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
              Quién recibirá el PDF
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                maxHeight: 280,
                borderColor: "#e5e7eb",
                boxShadow: "none",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1.25 }}>
                      Colaborador
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1.25 }}>
                      WhatsApp
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1.25 }}>
                      Sucursal
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc", py: 1.25 }}>
                      Puesto
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {faltasReportSelectedPartners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          {faltasReportPartnerLoad
                            ? "…"
                            : "Selecciona colaboradores."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    faltasReportSelectedPartners.map((p) => {
                      const wa = normalizeMxWhatsApp(p?.person?.phone);
                      return (
                        <TableRow key={p.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{partnerFullName(p)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" component="span" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              {wa ? formatPhoneForDisplay(wa) : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                            {toDisplayCase(p?.candidate?.branch?.name) || "—"}
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                            {toDisplayCase(p?.candidate?.vacancy?.position?.name) || "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 3 }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setModalFaltasReport(false)}
                disabled={faltasReportSending || faltasReportDownloading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                style={{ minWidth: 140 }}
                disabled={
                  faltasReportSending ||
                  faltasReportDownloading ||
                  faltasReportPartnerLoad
                }
                onClick={async () => {
                  setFaltasReportDownloading(true);
                  setLoading(true);
                  try {
                    const { blob, filename } =
                      await downloadFaltasReportConsolidadoPdf(
                        faltasReportDate,
                        faltasReportShift
                      );
                    const url = window.URL.createObjectURL(blob);
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = filename;
                    document.body.appendChild(anchor);
                    anchor.click();
                    anchor.remove();
                    window.URL.revokeObjectURL(url);
                    setSnackbar({
                      open: true,
                      message: "PDF descargado correctamente.",
                      severity: "success",
                    });
                  } catch (err: any) {
                    setSnackbar({
                      open: true,
                      message:
                        err?.response?.data?.message ||
                        err?.message ||
                        "Error al descargar el PDF.",
                      severity: "error",
                    });
                  } finally {
                    setFaltasReportDownloading(false);
                    setLoading(false);
                  }
                }}
              >
                {faltasReportDownloading ? "Descargando..." : "Descargar PDF"}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ minWidth: 140 }}
                disabled={
                  faltasReportSending ||
                  faltasReportDownloading ||
                  faltasReportPartnerLoad
                }
                onClick={async () => {
                  setFaltasReportSending(true);
                  setLoading(true);
                  try {
                    const phones = faltasReportSelectedPartners
                      .map((p) => normalizeMxWhatsApp(p.person?.phone))
                      .filter((x): x is string => Boolean(x));
                    const uniquePhones = [...new Set(phones)];
                    const result = await sendFaltasReportConsolidadoWhatsApp(
                      faltasReportDate,
                      faltasReportShift,
                      uniquePhones.length ? { phoneNumbers: uniquePhones } : undefined
                    );
                    if (result?.sent) {
                      let message = "Reporte consolidado enviado por WhatsApp.";
                      if (result.recipientsSent != null) {
                        message = `Enviado a ${result.recipientsSent} número(s).`;
                        if (result.recipientsFailed && result.recipientsFailed > 0) {
                          message += ` (${result.recipientsFailed} error(es))`;
                        }
                      }
                      setSnackbar({ open: true, message, severity: "success" });
                      setModalFaltasReport(false);
                    } else {
                      setSnackbar({
                        open: true,
                        message: "No se pudo enviar el reporte. Revisa la consola del servidor.",
                        severity: "error",
                      });
                    }
                  } catch (err: any) {
                    setSnackbar({
                      open: true,
                      message: err?.message || err?.response?.data?.message || "Error al enviar el reporte.",
                      severity: "error",
                    });
                  } finally {
                    setFaltasReportSending(false);
                    setLoading(false);
                  }
                }}
              >
                {faltasReportSending ? "Enviando…" : "Enviar reporte"}
              </button>
            </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        }
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ConfigSection;
